import React, { useEffect, useState } from 'react';
import { Box } from '@mui/material';
import { useCases } from '../../contexts/api-contexts/CasesContext';
import { useNavigation } from '../../contexts/NavigationContext';
import CaseBasicInfo from './CaseBasicInfo';
import CaseSymptoms from './CaseSymptoms';
import CaseDiagnoses from './CaseDiagnoses';
import CaseTreatments from './CaseTreatments';
import CaseSummary from './CaseSummary';
//import { WebSocketStatus } from '../common/WebSocketStatus';

export type CaseTab =
  | 'basic'
  | 'symptoms'
  | 'diagnoses'
  | 'treatments'
  | 'summary';

export interface CaseContentProps {
  id?: string;
  activeTab?: CaseTab;
  onTabChange?: (tab: CaseTab) => void;
  onNavigate?: (path: string) => void;
}

export const CaseContent = ({
  id,
  activeTab = 'basic',
  onTabChange,
  onNavigate,
}: CaseContentProps) => {
  const { fetchCase, currentCase } = useCases();
  const navigation = useNavigation();

  const [internalActiveTab, setInternalActiveTab] =
    useState<CaseTab>(activeTab);

  // Load case data if ID is provided
  console.log('id', id);
  console.log('currentCase', currentCase);
  useEffect(() => {
    if (id && currentCase && currentCase.id === id) return;
    if (id) {
      fetchCase(id).catch((err) => {
        console.error('Error fetching case data:', err);
      });
    }
    return () => {
      console.log('unmounting CaseContent');
      // Note: Case state is now managed internally by CasesContext
    };
  }, [id, fetchCase, currentCase]);

  // Update internal tab state when prop changes or navigation context changes
  useEffect(() => {
    setInternalActiveTab(activeTab);
  }, [activeTab]);

  useEffect(() => {
    setInternalActiveTab(navigation.navigationState.activeTab);
  }, [navigation.navigationState.activeTab]);

  // Handle tab changes
  const handleTabChange = (tab: CaseTab) => {
    setInternalActiveTab(tab);
    // Try context first, fallback to props
    navigation.navigateToTab(tab);
    onTabChange?.(tab);
  };

  // Handle navigation requests from child components
  const handleNavigate = (path: string) => {
    // Try context first, fallback to props
    navigation.navigateToPath(path);
    onNavigate?.(path);
  };

  // We've moved case creation to the CaseBasicInfo component
  // For now, we keep the isCreating state here to maintain compatibility

  // Render the active tab content
  const renderTabContent = () => {
    switch (internalActiveTab) {
      case 'symptoms':
        return (
          <CaseSymptoms
            onNavigate={handleNavigate}
            onTabChange={handleTabChange}
          />
        );
      case 'diagnoses':
        return (
          <CaseDiagnoses
            onNavigate={handleNavigate}
            onTabChange={handleTabChange}
          />
        );
      case 'treatments':
        return (
          <CaseTreatments
            id={id}
            onNavigate={handleNavigate}
            onTabChange={handleTabChange}
          />
        );
      case 'summary':
        return (
          <CaseSummary
            id={id}
            onNavigate={handleNavigate}
            onTabChange={handleTabChange}
          />
        );
      case 'basic':
      default:
        return (
          <CaseBasicInfo
            id={id}
            onNavigate={handleNavigate}
            onTabChange={handleTabChange}
          />
        );
    }
  };

  return (
    <Box sx={{ p: 1, height: '100%' }}>
      {/* <WebSocketStatus /> */}
      {renderTabContent()}
    </Box>
  );
};
